import { CylinderGeometry, ExtrudeGeometry, Shape } from 'three';
import { HEX_TILE_SIDES } from '../../constants';
import config from '../config';

const {
  radius: RADIUS,
  thickness: THICKNESS,
  waterThickness: WATER_THICKNESS,
} = config.hexMap.tile;
const BEVEL_SIZE = 0.07;
// The bevel's rim meets the water surface.
const BEVEL_HEIGHT = THICKNESS - WATER_THICKNESS;

/**
 * Land tile: a hex slab whose top edge slopes down all round, so neighbouring
 * tiles read individually from the lighting alone. Base on y = 0, top face on
 * y = THICKNESS, outer rim exactly the hex radius (tiles touch edge to edge).
 */
function createBevelledHex(): ExtrudeGeometry {
  // Pointy-top: vertices at ±y in shape space, which become ±z after rotating flat.
  const inner = RADIUS - BEVEL_SIZE;
  const shape = new Shape();
  for (let i = 0; i < 6; i++) {
    const a = Math.PI / 2 + (i * Math.PI) / 3;
    const x = inner * Math.cos(a);
    const y = inner * Math.sin(a);
    if (i === 0) shape.moveTo(x, y);
    else shape.lineTo(x, y);
  }
  shape.closePath();
  // ExtrudeGeometry bevels both caps, adding BEVEL_HEIGHT above and below `depth`.
  const geometry = new ExtrudeGeometry(shape, {
    depth: THICKNESS - 2 * BEVEL_HEIGHT,
    bevelEnabled: true,
    bevelSize: BEVEL_SIZE,
    bevelThickness: BEVEL_HEIGHT,
    bevelSegments: 2,
  });
  geometry.rotateX(-Math.PI / 2);
  geometry.translate(0, BEVEL_HEIGHT, 0);
  return geometry;
}

/** Explored land tiles. */
const bevelledGeometry = createBevelledHex();

/**
 * Water and fogged tiles: a flat slab whose top sits at the bevel's rim, so
 * land stands slightly proud of the sea. Fogged land uses it too, so neither
 * the bevel nor the height step gives away hidden coastlines.
 */
const flatGeometry = new CylinderGeometry(RADIUS, RADIUS, WATER_THICKNESS, HEX_TILE_SIDES);
flatGeometry.translate(0, WATER_THICKNESS / 2, 0);

export { bevelledGeometry, flatGeometry };
